import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import TNTable from "@/components/TNTable";
import { CButton, CCol, CFormInput, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslationContext } from "@/context/TranslationContext";
import { useListEmailTemplate, useEmailTemplateDelete } from "@/hooks/useEmailTemplate";
import { faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import "react-confirm-alert/src/react-confirm-alert.css";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import { masters } from "@/libs/routes";
import { emailTemplate } from "@/constants/translation/emailTemplate.translation";
import _ from "lodash";
import { setFormatDate } from "@/utils/dateFormate";
import ReadMore from "@/components/ReadMore";
import constants from "@/libs/constant";

/**
 * ListEmailTemplatePage Component
 *
 * This component is responsible for rendering and managing the list of email templates.
 * It includes functionalities such as searching, pagination, editing, and deleting email templates.
 * React component for adding a new email template.
 *
 * This component provides a form for adding email templates, including fields for different languages.
 * It fetches the list of languages and validates the form inputs before submitting the data.
 *
 * @returns {JSX.Element} React component for listing email templates.
 */
const ListEmailTemplatePage = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  // Set all permissions to true for now (as requested)
  const canView = true;
  const canAddEmailTemplate = true;
  const canUpdateEmailTemplate = true;
  const canDeleteEmailTemplate = true;

  /**
   * setting state values to default,
   */
  const [currentPage, setCurrentPage] = useState(constants.pagination.defaultPage);
  const [searchText, setSearchText] = useState("");

  // Clear localStorage and reset state when component unmounts
  useEffect(() => {
    return () => {
      // Clear localStorage
      localStorage.removeItem(constants.storageKeys.adminEmailTemplateTable);
      
      // Clear timeouts
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const [tempSearchText, setTempSearchText] = useState("");
  const searchTimeout = useRef(null);
  /**
   * This function will run when user enter something in search input
   */

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setTempSearchText(searchValue);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setSearchText(searchValue);
      setCurrentPage(1);
    }, 400); // Delay before updating real searchText
  };

  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);


  const {
    refetch,
    data: listData,
    isLoading,
  } = useListEmailTemplate({
    page: currentPage || constants.pagination.defaultPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText && searchText.trim() ? { search_text: searchText.trim() } : {}),
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  /**
   * This will clear all state values, and localstorage as well
   */
  const handleClearFilter = () => {
    setSearchText("");
    setCurrentPage(constants.pagination.defaultPage);
    setTempSearchText("");
  };
  const [paginationData, setPaginationData] = useState(null);
  /**
   * !This Function will call when user clicks on Edit Button
   * @param {*} tData which is current element of button
   */
  const handleEditClick = (tData) => {
    let email_template_id =
      tData.currentTarget.getAttribute("email_template_id");
    navigate(masters.emailTemplate.edit.replace(":id", email_template_id));
  };
  /**
   * This will call an API to delete email template
   */
  const { mutate: doDeleteEmailTemplate } = useEmailTemplateDelete(
    (response) => {
      toast.success(response.message || t(messages.toast.emailTemplate.deleted));
      refetch();
    }
  );

  /**
   * This function will call on click of delete button, and will display alert,
   * and will call status update API after confirmation
   */
  const handleDeleteClick = (tData) => {
    let email_template_id =
      tData.currentTarget.getAttribute("email_template_id");
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-box">
            <div className="alert-popup">
            <FontAwesomeIcon
                className="alert-close"
                icon={faClose}
                onClick={() => {
                  onClose();
                }}
              />
              <h2 className="alert-title">
                {t(emailTemplate.list.delete_popup_heading_text)}
              </h2>
              <div className="d-flex gap-2 justify-content-center">
                <CButton
                  color="primary"
                  className="table-delete-button"
                  onClick={() => {
                    doDeleteEmailTemplate(email_template_id);
                    onClose();
                  }}
                >
                  {t(emailTemplate.list.delete_popup_yes_btn_text)}
                </CButton>
                <CButton
                  className="table-primary-button"
                  color="danger"
                  onClick={onClose}
                >
                  {t(emailTemplate.list.delete_popup_no_btn_text)}
                </CButton>
              </div>
            </div>
          </div>
        );
      },
    });
  };
  /**
   * !This Block is making Headers for the column
   * @param Not Required
   */

  const columnsjson = [
    {
      id: "created_at", // Add an explicit ID
      accessorKey: "createdAt", // Use createdAt from API
      header: t(emailTemplate.list.table.created_at_table_col_heading),
      cell: ({ getValue }) => <span>{setFormatDate(getValue())}</span>, //  Use getValue() in v8
    },
    {
      id: "subject", // Add an explicit ID
      accessorKey: "id",
      header: t(emailTemplate.list.table.subject_table_col_heading),
      cell: ({ row }) => {
        const { templates } = row.original;

        if (_.isEmpty(templates)) return <div>—</div>; // Handle empty data
        return (
          <div>
            {_.map(templates, (template) => (
              <div key={template.id} className="d-flex gap-1">
                <strong>{`${template.language?.name || "N/A"}: `}</strong>
                <ReadMore>{template.subject || "N/A"}</ReadMore>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: "title", //  Add an explicit ID
      accessorKey: "id",
      header: t(emailTemplate.list.table.title_table_col_heading),
      cell: ({ row }) => {
        const { templates } = row.original;

        if (_.isEmpty(templates)) return <div>—</div>; // Handle empty data
        return (
          <div>
            {_.map(templates, (template) => (
              <div key={template.id} className="d-flex gap-1">
                <strong>{`${template.language?.name || "N/A"}: `}</strong>
                <ReadMore>{template.title || "N/A"}</ReadMore>
              </div>
            ))}
          </div>
        );
      },
    },

    {
      id: "action_2",
      accessorKey: "id",
      header: t(emailTemplate.list.table.action_table_col_heading),
      cell: ({ row }) => {
        return (
          <div className="d-flex gap-2 justify-content-center">
            {canUpdateEmailTemplate && (
              <div
                className="table-action-text"
                email_template_id={row.original.uuid}
                onClick={handleEditClick}
              >
                <FontAwesomeIcon
                  icon={faEdit}
                  className="list-icon"
                />
              </div>
            )}
            {canDeleteEmailTemplate && (
              <div
                className="table-action-text"
                email_template_id={row.original.uuid}
                onClick={handleDeleteClick}
              >
                <FontAwesomeIcon 
                  icon={faTrash}
                  className="list-icon"
                />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const [emailTemplateList, setEmailTemplateList] = useState([]);
  /**
   * !This API will call while Page Load and set data. Once data load we are updating State
   */

  useEffect(() => {
    if (listData && !isLoading) {
      setEmailTemplateList(listData.data.emailTemplatesList || []);
      setPaginationData(listData.data.pagination);
    }
  }, [listData, isLoading]);

  const columns = useMemo(() => columnsjson, []);
  // if (paginationData === null) {
  //   return null;
  // }
  /**
   * This function will set current page of table
   */
  const pageIndexHandle = (pageIndex) => {
    setCurrentPage(pageIndex + 1);
  };
  /**
   * This function will call on Add Email Template button Click and will take user to the add page of Email Templates
   */
  const addEmailTemplateHandle = () => {
    navigate(masters.emailTemplate.add);
  };

  /**
   * Breadcum Lebel and Link
   */

  // Check if user has view permission
  if (!canView) {
    return (
      <div className="text-center p-4">
        <p className="text-danger">{t(emailTemplate.list.no_permission_to_view)}</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <CRow className="list-align">
          <CCol lg={10} md={10} sm={12} xs={12} className="d-flex gap-2 justify-content-end flex-wrap">
            <CFormInput
              placeholder={t(emailTemplate.list.search_placeholder)}
              type={constants.inputTypes.text}
              name={constants.formFields.emailTemplate.searchText}
              value={tempSearchText}
              onChange={handleSearch}
              className="filter-field-space form-control-sm"
              style={{ maxWidth: '300px' }}
            />
            <CButton 
              color="primary" 
              type="button" 
              className="filter-field-space" 
              onClick={handleClearFilter}
              style={{ display: tempSearchText.trim() ? 'inline-block' : 'none' }}
            >
              {t(emailTemplate.list.clear_filter_btn_text)}
            </CButton>
            {canAddEmailTemplate && (
              <CButton 
                color="primary" 
                type="button" 
                className="filter-field-space" 
                onClick={addEmailTemplateHandle}
              >
                {t(emailTemplate.list.create_email_template_btn_text)}
              </CButton>
            )}
          </CCol>
        </CRow>
      </div>
      <TNTable
        columns={columns}
        data={emailTemplateList}
        paginationData={paginationData}
        onSelectPage={pageIndexHandle}
        t={t}
        pageIndexGet={currentPage - 1}
        key={Math.floor(Math.random() * (1000 - 1 + 1) + 1)}
        isLoading={isLoading}
        className="text-start table-sortable mt-3"
      />
    </>
  );
};
ListEmailTemplatePage.propTypes = {
  columns: PropTypes.any,
  row: PropTypes.any,
  data: PropTypes.any,
  paginationData: PropTypes.any,
  t: PropTypes.func,
  value: PropTypes.any,
};

// Static data that would have been passed via getStaticProps
ListEmailTemplatePage.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.emailTemplate?.list,
    pathname: masters.emailTemplate.list,
    active: true,
  },
];

export default ListEmailTemplatePage;
